import { useState } from 'react';
import { Num, T, useGT, useLocale, Var } from 'gt-react';
import { TableIcon } from '@phosphor-icons/react';
import {
  formatReportNumber,
  TABLE_COMPONENT_MAX_ROWS,
  type ReportTableColumn,
  type ReportValueFormat,
  type TableComponent,
} from '@erp/shared-types';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { defineComponentType, type ComponentRegistry } from '../registry';
import {
  useComponentDataset,
  useReportDataset,
  type ReportDatasetColumn,
  type ReportDatasetRow,
  type ReportDatasetValue,
} from '../ReportDataContext';
import { useIsFramed } from '../ComponentFrame';
import {
  columnFormat,
  fillPath,
  formatReportText,
  formatReportValue,
  rowTextContext,
} from '../format/reportText';
import { ReportLink } from '../ReportLink';
import { PERSISTENT_SCROLLBAR } from '@/lib/persistentScrollbar';
import { cn } from '@/lib/utils';
import { useReportColors } from '../palette/ReportColorPaletteContext';
import { ChartEmptyState, ConfigRow, DatasetSelectRow } from './shared';
import { colorScaleRanges, conditionalCellStyle } from './tableFormatting';
import { TableFormattingFields } from './TableFormattingFields';

/** A Table column's key: its own, or the field it shows; null for a text column. */
function columnKey(column: TableComponent['columns'][number]): string | null {
  return typeof column === 'string' ? column : (column.key ?? null);
}

/** The columns a Table shows: its chosen ones that the dataset has, in order, else every column. */
export function tableColumns(
  component: Pick<TableComponent, 'columns'>,
  columns: readonly ReportDatasetColumn[],
): ReportDatasetColumn[] {
  if (!component.columns.length) return [...columns];
  const byKey = new Map(columns.map((column) => [column.key, column]));
  return component.columns.flatMap((entry) => {
    const key = columnKey(entry);
    const column = key ? byKey.get(key) : undefined;
    return column ? [column] : [];
  });
}

/** A column as the Table draws it: what it reads and how. */
interface ShownColumn {
  /** Written out (label, links, a second line…), or just a key. */
  spec: ReportTableColumn;
  written: boolean;
  label: string;
  format: ReportValueFormat | null;
  numeric: boolean;
}

/**
 * The Table's columns as drawn: every dataset column until some are chosen;
 * a chosen key the dataset lacks is left out, a written-out column is kept.
 */
export function shownColumns(
  component: Pick<TableComponent, 'columns'>,
  columns: readonly ReportDatasetColumn[],
): ShownColumn[] {
  const byKey = new Map(columns.map((column) => [column.key, column]));
  const entries = component.columns.length
    ? component.columns
    : columns.map((column) => column.key);
  return entries.flatMap((entry) => {
    const written = typeof entry !== 'string';
    const spec: ReportTableColumn = written ? entry : { key: entry };
    const column = spec.key ? byKey.get(spec.key) : undefined;
    if (!written && !column) return [];
    return [
      {
        spec,
        written,
        label: spec.label ?? column?.label ?? spec.key ?? '',
        format: spec.format ?? column?.format ?? null,
        numeric: column?.dataType === 'number',
      },
    ];
  });
}

function numberOf(value: unknown): number {
  const number = typeof value === 'number' ? value : Number(value);
  return Number.isFinite(number) ? number : 0;
}

/**
 * Rows by group, as `rowOrder` asks: groups by their summed `by`, largest
 * first and an empty group last, then each group's rows by `by`.
 */
export function orderRows(
  rows: readonly ReportDatasetRow[],
  order: TableComponent['rowOrder'],
): ReportDatasetRow[] {
  if (!order) return [...rows];
  const totals = new Map<unknown, number>();
  for (const row of rows) {
    const group = row[order.group] ?? null;
    totals.set(group, (totals.get(group) ?? 0) + numberOf(row[order.by]));
  }
  const groupRank = (row: ReportDatasetRow) => {
    const group = row[order.group] ?? null;
    return group == null ? -Infinity : (totals.get(group) ?? 0);
  };
  return [...rows].sort(
    (a, b) =>
      groupRank(b) - groupRank(a) ||
      String(a[order.group]).localeCompare(String(b[order.group])) ||
      numberOf(b[order.by]) - numberOf(a[order.by]),
  );
}

function cellText(value: ReportDatasetValue | undefined): string {
  if (value === null || value === undefined) return '';
  if (typeof value === 'boolean') return value ? 'TRUE' : 'FALSE';
  return String(value);
}

/** A cell's text: its template, its value's label, or its value in the column's format. */
function shownValue(
  column: ShownColumn,
  row: ReportDatasetRow,
  context: {
    columns: readonly ReportDatasetColumn[];
    numberFormats: TableComponent['columnFormats'];
    locale: string;
  },
): string {
  const { spec } = column;
  if (spec.text != null)
    return formatReportText(
      spec.text,
      rowTextContext(row, context.columns, context.locale),
    );
  const value = spec.key ? (row[spec.key] ?? null) : null;
  if (value == null && spec.nullText != null) return spec.nullText;
  if (typeof value === 'string' && spec.valueLabels?.[value] != null)
    return spec.valueLabels[value];
  const numberFormat = spec.key ? context.numberFormats[spec.key] : undefined;
  if (numberFormat && typeof value === 'number')
    return formatReportNumber(value, numberFormat);
  if (column.format)
    return formatReportValue(value, column.format, context.locale);
  return cellText(value);
}

const isDate = (format: ReportValueFormat | null) =>
  format === 'date' || format === 'day';

/** A written-out column's cell: its text, link, second line and width. */
function WrittenCell({
  column,
  row,
  text,
  compact,
  locale,
  columns,
}: {
  column: ShownColumn;
  row: ReportDatasetRow;
  text: string;
  /** A headerless list: dates and long text in the small, quiet style. */
  compact: boolean;
  locale: string;
  columns: readonly ReportDatasetColumn[];
}) {
  const { spec } = column;
  const right = (spec.align ?? (column.numeric ? 'right' : 'left')) === 'right';
  const empty = spec.key != null && row[spec.key] == null;
  const href = spec.href ? fillPath(spec.href, row) : null;
  const main = spec.clamp ? (
    <p className="line-clamp-2" title={text}>
      {text}
    </p>
  ) : (
    text
  );
  const content = href ? (
    <ReportLink href={href} className="hover:underline">
      {main}
    </ReportLink>
  ) : (
    main
  );
  const secondary = spec.secondary;
  const secondaryValue = secondary ? (row[secondary.key] ?? null) : null;
  return (
    <TableCell
      className={cn(
        'py-1.5 align-top',
        right && 'text-right tabular-nums',
        isDate(column.format) && 'whitespace-nowrap',
        empty && !href && 'text-muted-foreground',
        compact && isDate(column.format) && 'text-xs text-muted-foreground',
        compact && (spec.clamp || right) && 'text-xs',
      )}
    >
      {spec.width ? (
        // A table cell's width is only a hint; a block inside it holds it.
        <div style={{ minWidth: `min(${spec.width}rem, 40cqw)` }}>
          {content}
        </div>
      ) : (
        content
      )}
      {secondary && secondaryValue != null && (
        <div className="text-xs text-muted-foreground tabular-nums">
          {formatReportValue(
            secondaryValue,
            secondary.format ?? columnFormat(columns, secondary.key),
            locale,
          )}
        </div>
      )}
    </TableCell>
  );
}

function TableRenderer({ component }: { component: TableComponent }) {
  const dataset = useComponentDataset(component);
  const colors = useReportColors();
  const locale = useLocale();
  const framed = useIsFramed();
  const [showAll, setShowAll] = useState(false);
  if (!dataset || !dataset.rows.length)
    return (
      <ChartEmptyState
        hasData={false}
        icon={<TableIcon className="h-8 w-8 text-muted-foreground" />}
      />
    );
  const columns = shownColumns(component, dataset.columns);
  const ordered = orderRows(dataset.rows, component.rowOrder).slice(
    0,
    component.maxRows,
  );
  const preview = component.previewRows;
  const rows = preview && !showAll ? ordered.slice(0, preview) : ordered;
  const ranges = colorScaleRanges(component.conditionalFormats, rows);
  const compact = component.hideHeader === true;
  const context = {
    columns: dataset.columns,
    numberFormats: component.columnFormats,
    locale,
  };
  const table = (
    <Table className={framed ? undefined : 'text-xs'}>
      {!compact && (
        <TableHeader sticky={!framed}>
          <TableRow>
            {columns.map((column, index) => (
              <TableHead
                key={index}
                className={cn(
                  'h-7',
                  // Written-out headers wrap, so a narrow table fits before it scrolls.
                  column.written ? 'whitespace-normal' : 'whitespace-nowrap',
                  column.written &&
                    (column.spec.align ??
                      (column.numeric ? 'right' : 'left')) === 'right' &&
                    'text-right',
                )}
              >
                {column.label}
              </TableHead>
            ))}
          </TableRow>
        </TableHeader>
      )}
      <TableBody>
        {rows.map((row, rowIndex) => (
          <TableRow key={rowIndex}>
            {columns.map((column, index) => {
              const text = shownValue(column, row, context);
              if (column.written)
                return (
                  <WrittenCell
                    key={index}
                    column={column}
                    row={row}
                    text={text}
                    compact={compact}
                    locale={locale}
                    columns={dataset.columns}
                  />
                );
              const key = column.spec.key ?? '';
              const painted = conditionalCellStyle(
                component.conditionalFormats,
                key,
                row[key] ?? null,
                ranges,
                colors.sequential,
              );
              return (
                <TableCell
                  key={index}
                  className={`whitespace-nowrap py-1 ${column.numeric ? 'text-right tabular-nums' : ''}`}
                  style={painted ?? undefined}
                >
                  {text}
                </TableCell>
              );
            })}
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
  const hidden = ordered.length - rows.length;
  return (
    <div className={cn('flex flex-col', !framed && 'min-h-0 flex-1')}>
      {framed ? (
        // Wider than the widget: it scrolls sideways, with a visible bar.
        <div className={cn('@container overflow-x-auto', PERSISTENT_SCROLLBAR)}>
          {table}
        </div>
      ) : (
        <div className="min-h-0 flex-1 overflow-auto">{table}</div>
      )}
      {(hidden > 0 || showAll) && (
        <Button
          variant="ghost"
          size="sm"
          className="h-7 self-end text-xs"
          onClick={() => setShowAll((value) => !value)}
        >
          {showAll ? (
            <T context="button, collapse a table to its first rows">
              Show Fewer
            </T>
          ) : (
            <T context="button, show every row of a table">
              Show All <Num>{ordered.length}</Num>
            </T>
          )}
        </Button>
      )}
      {dataset.rows.length > ordered.length && (
        <p className="shrink-0 px-2 py-1 text-[10px] text-muted-foreground">
          <T context="report table shows its leading rows">
            First <Var>{ordered.length.toLocaleString()}</Var> of{' '}
            <Var>{dataset.rows.length.toLocaleString()}</Var> rows
          </T>
        </p>
      )}
    </div>
  );
}

function TableConfigForm({
  component,
  onUpdate,
}: {
  component: TableComponent;
  onUpdate: (updated: TableComponent) => void;
}) {
  const gt = useGT();
  const dataset = useReportDataset(component.datasetKey);
  const columns = dataset?.columns ?? [];
  const shown = new Set(tableColumns(component, columns).map((c) => c.key));
  function toggle(key: string, checked: boolean) {
    const next = columns
      .map((column) => column.key)
      .filter((column) => (column === key ? checked : shown.has(column)));
    // A Table shows at least one column: none chosen would mean every column.
    if (!next.length) return;
    // Every column chosen is the same as none: show every column, new ones included.
    onUpdate({
      ...component,
      columns: next.length === columns.length ? [] : next,
    });
  }
  return (
    <div className="space-y-4">
      <DatasetSelectRow
        component={component}
        onDatasetChange={(datasetKey) =>
          onUpdate({ ...component, datasetKey, columns: [] })
        }
      />
      <ConfigRow label={gt('Columns', { context: 'report table columns' })}>
        <div className="max-h-48 space-y-1.5 overflow-y-auto">
          {columns.map((column) => (
            <div key={column.key} className="flex items-center gap-2">
              <Checkbox
                id={`table-column-${component.id}-${column.key}`}
                checked={shown.has(column.key)}
                onCheckedChange={(checked) =>
                  toggle(column.key, checked === true)
                }
              />
              <Label
                htmlFor={`table-column-${component.id}-${column.key}`}
                className="text-xs font-normal"
              >
                {column.label}
              </Label>
            </div>
          ))}
        </div>
      </ConfigRow>
      <ConfigRow
        label={gt('Rows shown', { context: 'report table row limit' })}
        htmlFor={`table-rows-${component.id}`}
      >
        <Input
          id={`table-rows-${component.id}`}
          type="number"
          min={1}
          max={TABLE_COMPONENT_MAX_ROWS}
          value={component.maxRows}
          onChange={(event) => {
            const value = Math.trunc(Number(event.target.value));
            if (value >= 1 && value <= TABLE_COMPONENT_MAX_ROWS)
              onUpdate({ ...component, maxRows: value });
          }}
        />
      </ConfigRow>
      <TableFormattingFields
        component={component}
        columns={columns}
        onUpdate={onUpdate}
      />
    </div>
  );
}

function TablePalettePreview() {
  return (
    <div className="flex h-24 items-center justify-center text-foreground">
      <svg viewBox="0 0 44 36" className="h-16 w-auto" fill="none">
        <rect
          x="4"
          y="4"
          width="36"
          height="28"
          rx="2"
          stroke="currentColor"
          strokeOpacity="0.4"
        />
        <rect
          x="4"
          y="4"
          width="36"
          height="6"
          fill="currentColor"
          fillOpacity="0.25"
        />
        {[16, 22, 28].map((y) => (
          <line
            key={y}
            x1="4"
            y1={y}
            x2="40"
            y2={y}
            stroke="currentColor"
            strokeOpacity="0.25"
          />
        ))}
        <line
          x1="18"
          y1="4"
          x2="18"
          y2="32"
          stroke="currentColor"
          strokeOpacity="0.25"
        />
      </svg>
    </div>
  );
}

export const tableDefinition = defineComponentType<TableComponent>({
  type: 'table',
  label: 'Table',
  defaultTitle: (_component, { dataset }) => dataset?.label ?? 'Table',
  description:
    'Dataset-bound table of rows: `columns` lists the columns shown in order (empty = every column), each a column key or written out as {key | text (a template over row fields, e.g. "{a} of {b}"), label?, format? (text|count|number|money|percent|percent100|days|day|date; absent = the column\'s own), align?, valueLabels?, nullText?, href? (an app path with {field} placeholders, making the cell a link), secondary? {key, format?}, clamp?, width?}; `previewRows` shows that many before a Show all; `hideHeader`; `rowOrder` {group, by} orders groups by their summed `by`; `maxRows` (1-1000) caps the leading rows shown; `columnFormats` maps a column to a number format {style, decimals?, currency?, compact?}; `conditionalFormats` paints cells: {kind: "threshold", column, op: gt|gte|lt|lte|eq|neq|between, value, value2?, tone: positive|negative|warning|neutral} or {kind: "color-scale", column}.',
  version: 1,
  size: { w: 12, h: 8, minW: 4, minH: 4 },
  make: (id) => ({
    type: 'table',
    id,
    columns: [],
    maxRows: 100,
    columnFormats: {},
    conditionalFormats: [],
  }),
  Renderer: TableRenderer,
  ConfigForm: TableConfigForm,
  Preview: TablePalettePreview,
});

/** The Table component, for surfaces that offer it beside the charts. */
export const tableWidgetRegistry: ComponentRegistry = {
  [tableDefinition.type]: tableDefinition,
};
